import axios from "axios";
import { useMjmlStore } from "@/stores/mjmlStore";
import { useNotificationsStore } from "@/stores/notifications";
import { CategoryAndKeyword } from "types/PremadeTemplates/PremadeTemplates";
import { api } from "@/api";

type ListParams = {
  per_page: number;
  current_page: number;
  search?: string;
  categories?: number[];
  keywords?: number[];
};

type GetParams = {
  params?: ListParams;
  headers?: Record<string, string>;
};

export default () => {
  const mjmlStore = useMjmlStore();
  const notificationsStore = useNotificationsStore();

  const apiHeader = {
    accept: "application/json",
    Authorization: `Bearer ${mjmlStore.authorize.apiKey}`,
  };

  const getData = async (
    currentPage: number,
    search: string,
    perPage: number,
    categories: number[],
    keywords: number[]
  ) => {
    if (!api.PREMADE_TEMPLATES) {
      notificationsStore.createNotification({
        text: "No api url for premade templates",
        type: "error",
      });
      return null;
    }

    const params: ListParams = {
      per_page: perPage,
      current_page: currentPage,
    };

    if (search !== "") params["search"] = search;
    if (categories.length > 0) params["categories"] = categories;
    if (keywords.length > 0) params["keywords"] = keywords;

    const getParams: GetParams = {
      params: params,
    };

    if (api.PREMADE_TEMPLATES.includes("app.topol.io")) {
      getParams["headers"] = apiHeader;
    }

    try {
      const response = await axios.get(api.PREMADE_TEMPLATES, getParams);

      if (response.data.success) {
        return response.data.data;
      } else {
        notificationsStore.createNotification({
          text: "Fetching premade templates failed",
          type: "error",
        });
        return null;
      }
    } catch (error) {
      notificationsStore.createNotification({
        text: "Fetching premade templates failed",
        type: "error",
      });
      return null;
    }
  };

  const getCategories = async (): Promise<CategoryAndKeyword[] | []> => {
    if (!api.PREMADE_TEMPLATE_CATEGORIES) {
      notificationsStore.createNotification({
        text: "No api url for categories",
        type: "error",
      });
      return [];
    }

    const getParams: GetParams = {};

    if (api.PREMADE_TEMPLATE_CATEGORIES.includes("app.topol.io")) {
      getParams["headers"] = apiHeader;
    }

    try {
      const responseCtegories = await axios.get(
        api.PREMADE_TEMPLATE_CATEGORIES,
        getParams
      );

      if (responseCtegories.data.success) {
        return responseCtegories.data.data;
      }

      notificationsStore.createNotification({
        text: "Fetching categories failed",
        type: "error",
      });
      return [];
    } catch (error) {
      notificationsStore.createNotification({
        text: "Fetching categories failed",
        type: "error",
      });
      return [];
    }
  };

  const getKeywords = async (): Promise<CategoryAndKeyword[] | []> => {
    if (!api.PREMADE_TEMPLATES_KEYWORDS) {
      notificationsStore.createNotification({
        text: "No api url for keywords",
        type: "error",
      });
      return [];
    }

    const getParams: GetParams = {};

    if (api.PREMADE_TEMPLATES_KEYWORDS.includes("app.topol.io")) {
      getParams["headers"] = apiHeader;
    }

    try {
      const responseKeywords = await axios.get(
        api.PREMADE_TEMPLATES_KEYWORDS,
        getParams
      );

      if (responseKeywords.data.success) {
        return responseKeywords.data.data;
      }

      notificationsStore.createNotification({
        text: "Fetching keywords failed",
        type: "error",
      });
      return [];
    } catch (error) {
      notificationsStore.createNotification({
        text: "Fetching keywords failed",
        type: "error",
      });
      return [];
    }
  };

  const getTemplate = async (id: number) => {
    if (!api.PREMADE_TEMPLATES) {
      notificationsStore.createNotification({
        text: "No api url for premade templates",
        type: "error",
      });
      return null;
    }

    const getParams: GetParams = {};

    if (api.PREMADE_TEMPLATES.includes("app.topol.io")) {
      getParams["headers"] = apiHeader;
    }

    const url = api.PREMADE_TEMPLATES.includes("?")
      ? `${api.PREMADE_TEMPLATES.split("?")[0]}/${id}`
      : `${api.PREMADE_TEMPLATES}/${id}`;

    try {
      const response = await axios.get(url, getParams);

      if (response.data.success) {
        return response.data.data;
      }

      notificationsStore.createNotification({
        text: "Fetching premade template failed",
        type: "error",
      });
      return null;
    } catch (error) {
      notificationsStore.createNotification({
        text: "Fetching premade template failed",
        type: "error",
      });
      return null;
    }
  };

  const deleteTemplate = async (id: number) => {
    if (!api.PREMADE_TEMPLATES) {
      notificationsStore.createNotification({
        text: "No api url for premade templates",
        type: "error",
      });
      return null;
    }

    const getParams: GetParams = {};

    if (api.PREMADE_TEMPLATES.includes("app.topol.io")) {
      getParams["headers"] = apiHeader;
    }

    const url = api.PREMADE_TEMPLATES.includes("?")
      ? `${api.PREMADE_TEMPLATES.split("?")[0]}/${id}`
      : `${api.PREMADE_TEMPLATES}/${id}`;

    try {
      const response = await axios.delete(url, getParams);

      if (response.data.success) {
        return true;
      }

      notificationsStore.createNotification({
        text: "Deleting premade template failed",
        type: "error",
      });
      return null;
    } catch (error) {
      notificationsStore.createNotification({
        text: "Deleting premade template failed",
        type: "error",
      });
      return null;
    }
  };

  return {
    getData,
    getCategories,
    getKeywords,
    getTemplate,
    deleteTemplate,
  };
};
